import React, { useEffect, useState } from 'react';
import { Box, Center, FlatList, Text, Pressable, Spinner, useTheme } from 'native-base';
import Reply from './Reply';
import { useAuthState } from '../../../context/auth';
import { useEnvState } from '../../../context/env';
import { useTopicsState, topicsAPI } from '../../../context/topics';
import { getLikedReplyIDs, uniqueReplyReducer } from '../../utils/helpers';
import { MIN_REPLIES_TO_SHOW, MAX_REPLIES_PER_BATCH } from '../../Constants';
const Replies = _ref => {
  let {
    topic,
    loadContents,
    updateTopicReplies,
    likedReplyIDs,
    setLikedReplyIDs,
    addLikedReplyID,
    removeLikedReplyID,
    navigation,
    platformUser,
    showUserProfile,
    setPassedReactRef,
    forumKey,
    forSingleTopicScreen,
    forceReloadToggle,
    trackForum
  } = _ref;
  const theme = useTheme(),
    {
      colors
    } = theme;
  const authState = useAuthState();
  const envState = useEnvState();
  const topicsState = useTopicsState();
  const {
    refresh_replies_topic_id,
    refresh_replies_topic_timestamp,
    flagged_post_id
  } = topicsState;
  const initialReplies = (topic === null || topic === void 0 ? void 0 : topic.recent.map(r => r.post)) || [];
  const [repliesList, setRepliesList] = useState(initialReplies);
  const [repliesStream, setRepliesStream] = useState([]);
  const [repliesToShow, setRepliesToShow] = useState(repliesList);
  // minus 1 to take off the first post.
  const [numOfRemainingReplies, setNumOfRemainingReplies] = useState(topic.posts_count - initialReplies.length - 1);
  const [collapsible, setCollapsible] = useState(false);
  const [loadMoreRepliesSpinner, setLoadMoreRepliesSpinner] = useState(false);
  const apiKey = platformUser !== null && platformUser !== void 0 && platformUser.discourseKey ? platformUser === null || platformUser === void 0 ? void 0 : platformUser.discourseKey : authState.api_key;
  const showRemainingRepliesString = () => {
    return numOfRemainingReplies > 0 ? `Tap here to read ${numOfRemainingReplies} more replies...` : '';
  };
  useEffect(() => {
    if (repliesList.length == topic.posts_count - 1 && repliesToShow.length == repliesList.length && repliesToShow.length > MIN_REPLIES_TO_SHOW) {
      setCollapsible(true);
      setInformationText('Collapse');
    } else {
      setInformationText(showRemainingRepliesString());
    }
  }, [numOfRemainingReplies, repliesToShow]);
  useEffect(() => {
    setRepliesList(initialReplies);
    setRepliesToShow(initialReplies);
  }, [refresh_replies_topic_id, refresh_replies_topic_timestamp, topic === null || topic === void 0 ? void 0 : topic.posts_count, flagged_post_id, forceReloadToggle]);
  const [informationText, setInformationText] = useState(showRemainingRepliesString());
  const getFirstReplyBatch = async () => {
    try {
      // minus 1 as first post isn't part of replies
      if (repliesToShow.length < topic.posts_count - 1) {
        const response = await topicsAPI.getSingleTopic(envState.base_url, apiKey, topic === null || topic === void 0 ? void 0 : topic.id);
        if (response && response.post_stream) {
          const {
            posts,
            stream
          } = response.post_stream;
          if (posts && stream) {
            let initSetOfReplies = posts.slice(1);

            /* we have to be careful here, if a topic has more than 20 replies
             * then the sortedReplies don't necessarily include what is in the initial repliesList */
            let sortedRepliesSoFar = uniqueReplyReducer([...initSetOfReplies, ...repliesList]);
            let newRemainingReplies = Math.max(0, topic.posts_count - sortedRepliesSoFar.length - 1);
            setRepliesList(sortedRepliesSoFar);
            const liked = getLikedReplyIDs(posts);
            setLikedReplyIDs(liked);
            setRepliesStream(stream);
            setNumOfRemainingReplies(newRemainingReplies);
            expandReplies(sortedRepliesSoFar); // explicitly call due to async state set
            return response;
          }
        }
        throw new Error('Failed to get single topic.');
      }
    } catch (err) {
      console.log('Error while loading the topic');
    }
  };
  const loadMoreReplies = async () => {
    // figure out the next batch of replies to get
    const loadedReplyIDs = repliesList.map(r => r.id);
    let remainingReplies = repliesStream.filter(s => !loadedReplyIDs.includes(s));
    if (remainingReplies.length > 0) {
      setLoadMoreRepliesSpinner(true);
      const nextBatch = remainingReplies.slice(0, MAX_REPLIES_PER_BATCH);
      try {
        const response = await topicsAPI.getSingleTopicMoreReplies(envState.base_url, apiKey, topic === null || topic === void 0 ? void 0 : topic.id, nextBatch);
        if (response && response.post_stream) {
          const {
            posts
          } = response.post_stream;
          if (posts) {
            /* even though uniqueReplyReducer below also does a sort
             * we still have to do this because the API call above
             * returns us the first post, so we have to shave it back off */
            const sortedReplies = posts.sort((a, b) => a.post_number - b.post_number).slice(1);
            let sortedRepliesSoFar = uniqueReplyReducer([...repliesList, ...sortedReplies]);
            let newRemainingReplies = Math.max(0, topic.posts_count - sortedRepliesSoFar.length - 1);
            let shownRepliesSoFar = uniqueReplyReducer([...repliesToShow, ...sortedReplies]);
            setRepliesList(sortedRepliesSoFar);
            setRepliesToShow(shownRepliesSoFar);
            setNumOfRemainingReplies(newRemainingReplies);
            setLoadMoreRepliesSpinner(false);
            return response;
          }
        }
        throw new Error('Failed to get more replies for single topic.');
      } catch (err) {
        console.log('Error while loading more replies');
      }
    } else {
      console.log('No more replies');
      setNumOfRemainingReplies(0);
      setLoadMoreRepliesSpinner(false);
    }
    return null;
  };
  const resetShownReplies = async () => {
    if (!repliesStream.length) {
      await getFirstReplyBatch();
    } else {
      await loadMoreReplies();
    }
    if (collapsible) {
      collapseReplies();
    } else {
      if (repliesList.length == topic.posts_count - 1) {
        expandReplies(); // otherwise, the awaiting call above is still pending
      }
    }
  };

  const expandReplies = rList => {
    setRepliesToShow(rList || repliesList);
  };
  const collapseReplies = () => {
    setCollapsible(false);
    /* we should only see this if we have more than MIN_REPLIES_TO_SHOW replies
     * so it is safe to use */
    setRepliesToShow(repliesList.slice(-MIN_REPLIES_TO_SHOW));
    setNumOfRemainingReplies(topic.posts_count - MIN_REPLIES_TO_SHOW - 1);
  };
  const headerComponent = /*#__PURE__*/React.createElement(Box, {
    py: 2,
    alignItems: 'center',
    justifyContent: 'center'
  }, /*#__PURE__*/React.createElement(Pressable, {
    onPress: () => resetShownReplies()
  }, /*#__PURE__*/React.createElement(Text, null, informationText)));
  const renderItem = _ref2 => {
    let {
      item: reply
    } = _ref2;
    return /*#__PURE__*/React.createElement(Reply, {
      reply: reply.post ? reply.post : reply,
      key: reply.id.toString(),
      updateTopicReplies: updateTopicReplies,
      loadContents: loadContents,
      likedReplyIDs: likedReplyIDs,
      addLikedReplyID: addLikedReplyID,
      removeLikedReplyID: removeLikedReplyID,
      navigation: navigation,
      showUserProfile: showUserProfile,
      forSingleTopicScreen: forSingleTopicScreen,
      forumKey: forumKey,
      setPassedReactRef: setPassedReactRef,
      trackForum: trackForum
    });
  };
  return /*#__PURE__*/React.createElement(Box, {
    w: "100%",
    px: 4,
    pt: 4,
    pb: 2,
    bg: colors.replies.bg.default,
    borderRadius: {
      base: 0,
      lg: 8
    }
  }, /*#__PURE__*/React.createElement(FlatList, {
    data: repliesToShow,
    extraData: repliesToShow,
    renderItem: renderItem,
    keyExtractor: reply => reply.id.toString(),
    ListHeaderComponent: informationText ? headerComponent : null
  }), loadMoreRepliesSpinner && /*#__PURE__*/React.createElement(Center, {
    flex: 1
  }, /*#__PURE__*/React.createElement(Spinner, {
    mb: 3
  }), /*#__PURE__*/React.createElement(Text, null, "Loading More Posts...")));
};
export default Replies;
//# sourceMappingURL=Replies.js.map